import * as d3 from "d3";
import * as utilities from "../utilities.js"
import data_processing from "./index.js";

/**
 * Combines values from a specified column over a given number of days,
 * using a custom extractor function to determine how to process the column data.
 *
 * @param {string} update_time - The starting date string (YYYY-MM-DD).
 * @param {string} column - The column name to extract values from.
 * @param {number} remaining_days - The number of days to combine values for.
 * @param {function} extractor - A function that receives the cell value and returns the processed value.
 * @returns {Array} - An array of combined data objects with date and value properties.
 */
async function combine_data(update_time, column, remaining_days, extractor) {
    let combined_data = [];
    let update_month = update_time.slice(0, 7);

    while (remaining_days > 0) {
        const filepath = `dataset/stats_data/${update_month}/${update_month}.csv.gz`;
        const exists = await utilities.check_file_exists(filepath);
        if (!exists) break;

        try {
            const stats_data = await utilities.load_daily_stats_data(update_month);
            for (let i = 0; i < stats_data.length && remaining_days > 0; i++) {
                const value = stats_data[i][column];

                if (value !== undefined && value !== "") {
                    const upd_time = new Date(stats_data[i].update_time);

                    combined_data.push({
                        date: upd_time,
                        value: extractor(value)
                    });
                }

                remaining_days--;
            }
        } catch (error) {
            console.error(`Error reading or processing file ${filepath}:`, error);
            break;
        }

        update_month = get_previous_month(update_month);
    }

    return combined_data;
}

/**
 * Combines data over a specified time scale and column.
 *
 * @param {string} time_scale - The time scale to combine data over (e.g., '30days', '90days', '180days', '1year').
 * @param {string} column - The column name to combine data from.
 * @returns {Array} - An array of combined data objects with date and value properties.
 */
export async function combine_data_over_time(time_scale, column) {
    let data_length = 0;
    const update_time = await data_processing.update_time.get_update_time();

    switch (time_scale) {
        case '30days':
            data_length = 30;
            break;
        case '90days':
            data_length = 90;
            break;
        case '180days':
            data_length = 180;
            break;
        case '1year':
            data_length = 360;
            break;
        case 'all_time':
            const upd_time_Date = new Date(update_time)
            const init_time_Date = new Date("2024-05-18")
            let time_between = upd_time_Date.getTime() - init_time_Date.getTime();
            data_length = Math.ceil(time_between / (1000*24*60*60))
            break;
    }

    let combined_data = [];
    let remaining_days = data_length;
    
    if (column == "num_datasets" || column == "num_resources" || column == "total_views") {
        combined_data = await combine_data(update_time, column, remaining_days, val => val);
        //await combine_value(update_time, column, remaining_days)
    } else {
        combined_data = await combine_data(update_time, column, remaining_days, val => JSON.parse(val).length);
        //combine_length(update_time, column, remaining_days)
    }

    combined_data.sort(function (a, b) {
        return new Date(a.date) - new Date(b.date);
    });
    
    return combined_data;
}

/**
 * Combines column data over a specified time scale.
 *
 * @param {string} time_scale - The time scale to combine data over (e.g., '30days', '90days', '180days', '1year').
 * @param {string} column - The column name to combine data from.
 * @returns {Array} - An array of combined data objects with date, display_name, and count properties.
 */
export async function combine_col_over_time(time_scale, column) {
    const update_time = await data_processing.update_time.get_update_time();
    let data_length = 0;

    switch (time_scale) {
        case '30days':
            data_length = 30;
            break;
        case '90days':
            data_length = 90;
            break;
        case '180days':
            data_length = 180;
            break;
        case '1year':
            data_length = 360;
            break;
        case 'all_time':
            const upd_time_Date = new Date(update_time)
            const init_time_Date = new Date("2024-05-18")
            let time_between = upd_time_Date.getTime() - init_time_Date.getTime();
            data_length = Math.ceil(time_between / (1000*24*60*60))
            break;
    }

    let combined_data = [];
    let remaining_days = data_length;
    let update_month = update_time.slice(0, 7);
    
    while (remaining_days > 0) {
        const filepath = `dataset/stats_data/${update_month}/${update_month}.csv.gz`;
        let stats_data = "";

        // Check if file exists
        const exists = await utilities.check_file_exists(filepath); 
        if (exists) {
            //stats_data = await d3.csv(filepath);
            stats_data = await utilities.load_daily_stats_data(update_month);
        } else {
            break;
        }

        // Combine
        try {     
            // Iterate data by date
            for (let i = 0; i < stats_data.length; i++) {
                if (remaining_days > 0) {
                    // Check if row data can be parsed
                    const cur_data = stats_data[i][column];
                    let row = "";

                    if (utilities.is_valid_JSON(cur_data)) {
                        row = JSON.parse(cur_data);
                    } else {
                        remaining_days = 0;
                        break;
                    }
                    // Split data
                    const upd_time = new Date(stats_data[i].update_time);
                    for (let item of row) {
                        let display_name = item['display_name'];

                        if(column == "keywords" && item.zh_tw_label) { // Some are undifined
                            display_name = item['display_name'] + " " + item['zh_tw_label']['value'];
                        }

                        combined_data.push({
                            date: upd_time,
                            display_name: display_name,
                            count: item['count']
                        });
                    }
                    remaining_days--;
                } else {
                    break;
                }
            }

        } catch (error) {
            break;
        }
        // Move to Last Month
        update_month = get_previous_month(update_month);
    }
    
    combined_data.sort(function (a, b) {
        return new Date(a.date) - new Date(b.date);
    });

    return combined_data;
}

/**
 * Helper function to get the previous month in 'yyyy-mm' format.
 *
 * @param {string} currentMonth - The current month in 'yyyy-mm' format.
 * @returns {string} - The previous month in 'yyyy-mm' format.
 */
function get_previous_month(currentMonth) {
    const [year, month] = currentMonth.split('-').map(Number);
    const date = new Date(year, month - 1); // Create a Date object with the current month
    date.setMonth(date.getMonth() - 1); // Move to the previous month
    const previousYear = date.getFullYear();
    const previousMonth = String(date.getMonth() + 1).padStart(2, '0'); // Get the month and pad with leading zero if necessary
    return `${previousYear}-${previousMonth}`;
}
